import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  useModulesCatalog,
  useUserPermissions,
  useUpdateUserPermissions,
} from "http-hooks/companyAdmin/useCompanyAdmin";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

const LEVEL_OPTIONS = [
  { value: 0, label: "None", color: "bg-gray-100 text-gray-500 border-gray-200" },
  { value: 10, label: "View", color: "bg-blue-100 text-blue-700 border-blue-300" },
  { value: 20, label: "Edit", color: "bg-yellow-100 text-yellow-700 border-yellow-300" },
  { value: 30, label: "Delete", color: "bg-red-100 text-red-700 border-red-300" },
];

export default function CompanyUserRoles() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userId = searchParams.get("userId");
  const userName = searchParams.get("name") || "User";

  const { data: catalog, isLoading: isCatalogLoading } = useModulesCatalog();
  const { data: savedPermissions, isLoading: isPermLoading } = useUserPermissions(userId);
  const { mutateAsync: savePermissions, isPending: isSaving } = useUpdateUserPermissions();

  const [permissions, setPermissions] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (savedPermissions) {
      setPermissions(savedPermissions);
      setHasChanges(false);
    }
  }, [savedPermissions]);

  if (!userId) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No user selected. Go back to Users and click the shield icon.</p>
        <button
          onClick={() => navigate("/company/users")}
          className="mt-4 text-indigo-600 hover:text-indigo-500 font-medium"
        >
          ← Back to Users
        </button>
      </div>
    );
  }

  const isLoading = isCatalogLoading || isPermLoading;

  const setLevel = (moduleCode, level) => {
    setPermissions((prev) => ({ ...prev, [moduleCode]: level }));
    setHasChanges(true);
  };

  const setGroupLevel = (groupModules, level) => {
    setPermissions((prev) => {
      const updated = { ...prev };
      for (const mod of groupModules) {
        updated[mod.code] = level;
      }
      return updated;
    });
    setHasChanges(true);
  };

  const setAllLevel = (level) => {
    if (!catalog) return;
    setPermissions((prev) => {
      const updated = { ...prev };
      for (const group of Object.values(catalog)) {
        for (const mod of group) {
          updated[mod.code] = level;
        }
      }
      return updated;
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      await savePermissions({ userId, data: { permissions } });
      setHasChanges(false);
    } catch (error) {
      console.error("Save failed:", error);
    }
  };

  const getActiveColor = (moduleCode, levelValue) => {
    const current = permissions[moduleCode] || 0;
    if (current !== levelValue) return "bg-white text-gray-400 border-gray-200 hover:bg-gray-50";
    return LEVEL_OPTIONS.find((o) => o.value === levelValue)?.color || "";
  };

  return (
    <div>
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/company/users")}
            className="rounded-md p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-2xl font-bold leading-7 text-gray-900">
              Module Permissions
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Configure access for <span className="font-semibold text-gray-700">{userName}</span>
            </p>
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-3">
          {hasChanges && (
            <span className="text-sm text-amber-600 font-medium">Unsaved changes</span>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? "Saving..." : "Save Permissions"}
          </button>
        </div>
      </div>

      {/* Bulk actions */}
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <span className="text-sm text-gray-500 font-medium mr-2">Set all:</span>
        {LEVEL_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setAllLevel(opt.value)}
            className={`px-3 py-1 text-xs font-medium rounded-md border ${opt.color} hover:opacity-80`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Permission matrix */}
      {isLoading ? (
        <div className="py-12 text-center text-gray-400">Loading modules...</div>
      ) : !catalog ? (
        <div className="py-12 text-center text-gray-400">Unable to load modules catalog</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(catalog).map(([groupName, modules]) => (
            <div key={groupName} className="rounded-lg border border-gray-200 overflow-hidden">
              {/* Group header */}
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">{groupName}</h3>
                <div className="flex gap-1">
                  {LEVEL_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setGroupLevel(modules, opt.value)}
                      className="px-2 py-0.5 text-xs font-medium rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-100"
                      title={`Set all in ${groupName} to ${opt.label}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Module rows */}
              <div className="divide-y divide-gray-100">
                {modules.map((mod) => (
                  <div
                    key={mod.code}
                    className="px-4 py-2.5 flex items-center justify-between hover:bg-gray-50"
                  >
                    <span className="text-sm text-gray-700">{mod.label}</span>
                    <div className="flex gap-1">
                      {LEVEL_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setLevel(mod.code, opt.value)}
                          className={`px-2.5 py-1 text-xs font-medium rounded border transition-colors ${getActiveColor(mod.code, opt.value)}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sticky save bar */}
      {hasChanges && (
        <div className="sticky bottom-0 bg-white border-t border-gray-200 py-3 mt-6 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="rounded-md bg-indigo-600 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
          >
            {isSaving ? "Saving..." : "Save Permissions"}
          </button>
        </div>
      )}
    </div>
  );
}
